<template>
  <div class="revision-card-list px-6">
    <template v-if="loading">
      <revision-card-skeleton-loader v-for="i in 3" :key="i" />
    </template>
    <template v-else>
      <revision-card
        v-for="revision in revisions"
        :key="revision.id"
        :revision="revision"
        @show-prisma-stats="onShowPrismaStats"
        @export-revision="onExportRevision"
        @publish-revision="onPublishRevision"
      />
    </template>
    <prisma-stats-dialog
      v-if="showPrismaStats"
      v-model="showPrismaModal"
      :prisma-stats="showPrismaStats"
    />
    <export-review-dialog
      v-model="showExportModal"
      :fields="review.dataExtractionFields"
      @export="onConfirmExportRevision"
    />
    <revision-publish-modal
      :open.sync="showPublishModal"
      :has-pending="revisionToPublishHasPending"
      :has-included="revisionToPublishHasIncluded"
      @publish-revision="onConfirmPublish"
    />
  </div>
</template>

<script>
import PrismaStatsDialog from '@/components/ReviewPage/PrismaStatsController/PrismaStatsDialog';
import ExportReviewDialog from '@/components/ReviewPage/ExportReviewDialog';
import RevisionPublishModal from './RevisionPublishModal';
import RevisionCard from './RevisionCard';
import RevisionCardSkeletonLoader from './RevisionCardSkeletonLoader';
import { Review, Revision } from '@/models';
import { GetRevisionsByReviewId } from '@/graphql';
import { mapActions } from 'vuex';

export default {
  name: 'RevisionCardList',
  components: {
    RevisionCard,
    RevisionCardSkeletonLoader,
    PrismaStatsDialog,
    ExportReviewDialog,
    RevisionPublishModal,
  },
  props: {
    review: {
      type: Review,
      required: true,
    },
  },
  data: () => ({
    revisions: null,
    showPrismaModal: false,
    showPrismaStats: null,
    showExportModal: false,
    revisionToExport: null,
    showPublishModal: false,
    revisionToPublish: null,
  }),
  apollo: {
    revisions: {
      query: GetRevisionsByReviewId,
      variables() {
        return {
          reviewId: this.review.id,
        };
      },
      update(data) {
        return data.revisionsByReviewId.map(
          (revision) => new Revision(revision)
        );
      },
      fetchPolicy: 'cache-and-network',
      notifyOnNetworkStatusChange: true,
    },
  },
  computed: {
    loading() {
      return this.$apollo.queries.revisions.loading;
    },
    revisionToPublishHasPending() {
      return this.revisionToPublish?.prismaStats.totalPending() > 0;
    },
    revisionToPublishHasIncluded() {
      return this.revisionToPublish?.prismaStats.reportsIncluded > 0;
    },
  },
  methods: {
    ...mapActions({
      exportReview: 'review/exportReview',
      publishRevision: 'revision/publish',
    }),
    onShowPrismaStats(prismaStats) {
      this.showPrismaStats = prismaStats;
      this.showPrismaModal = true;
    },
    onExportRevision(revision) {
      this.revisionToExport = revision;
      this.showExportModal = true;
    },
    onPublishRevision(revision) {
      this.showPublishModal = true;
      this.revisionToPublish = revision;
    },
    async onConfirmExportRevision({ groupByField, options }) {
      try {
        const downloadUrl = await this.exportReview({
          review: this.review,
          revision: this.revisionToExport,
          field: groupByField,
          options,
        });
        if (downloadUrl) window.location.href = downloadUrl;
      } finally {
        this.showExportModal = false;
      }
    },
    async onConfirmPublish(outcome) {
      try {
        await this.publishRevision({
          revision: this.revisionToPublish,
          outcome,
        });
      } finally {
        this.showPublishModal = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.revision-card-list {
  display: grid;
  grid-template-columns: minmax(1000px, 1200px);
  gap: 24px;
}
</style>
